import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as RPointerEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RANK_NAMES, formatDate } from '@pk/shared';
import type { ArmyView, Id, Road, TitleSummary, WorldView } from '@pk/shared';
import { actorOf, memberIds, useCommand, useHouse, useWorld } from '../api/hooks';
import { useUi } from '../store';
import type { MapMode } from '../store';
import { Badge, Btn, ErrorBox, Inline, Loading, Segmented } from '../components/ui';
import { IconChevron, IconCrown, IconRealm, IconShield, IconX } from '../components/icons';
import { Heraldry } from '../components/Heraldry';
import { HolderLine, useEta, useHourEta } from '../components/game';
import { daysFrom, shortestPath } from '../lib/roads';
import { formatEta, msForDays } from '../lib/eta';
import { mix, shade } from '../lib/color';
import { ancestorOfRank, buildLand, edgePaths, groupEdges, roadControl, terrainGlyphs } from '../lib/geometry';
import type { Glyph, LandCell, Pt } from '../lib/geometry';
import { RealmsPanel } from './RealmsPanel';
import { SettlePanel } from './Frontier';
import { MilitaryScreen } from './Military';
import '../styles/map.css';

const TERRAIN_FILL: Record<string, string> = {
  plains: '#9ccf63', farmland: '#d9cd62', hills: '#bfae5f', forest: '#58a057', mountains: '#a7aebb', marsh: '#74b5a0', coast: '#ead9a0',
};
const INK = '#0b1b33';
const GOLD = '#ffd46b';
const ENEMY = '#ff6b62';
const NEUTRAL = '#c9d2de';
const MIN_ZOOM = 0.8;
const MAX_ZOOM = 10;
const TOP_INSET = 122; // px reserved for the view/mode switches when fitting
const BOTTOM_INSET = 14;

interface View { cx: number; cy: number; zoom: number }
type Panel = { kind: 'holding'; id: Id } | { kind: 'army'; id: Id } | null;

interface Item {
  id: Id; name: string; x: number; y: number; terrain: string;
  kind: 'keep' | 'city' | 'site' | 'citysite'; tier: number; mine: boolean; accent: string; siege: boolean; dev: number;
}

const h01 = (s: string) => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return (h >>> 0) / 4294967296;
};
/** HSL (degrees, 0..1, 0..1) to hex. */
function hsl(h: number, sat: number, l: number): string {
  const hh = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * sat;
  const x = c * (1 - Math.abs(((hh / 60) % 2) - 1));
  const m = l - c / 2;
  const [rr, gg, bb] = hh < 60 ? [c, x, 0] : hh < 120 ? [x, c, 0] : hh < 180 ? [0, c, x] : hh < 240 ? [0, x, c] : hh < 300 ? [x, 0, c] : [c, 0, x];
  const to = (n: number) => Math.round((n + m) * 255).toString(16).padStart(2, '0');
  return `#${to(rr)}${to(gg)}${to(bb)}`;
}
/** One clear hue per kingdom (none near gold, which is reserved for "you"). */
const KINGDOM_HUES = [150, 2, 274, 26, 184, 326, 96, 214];
/** How each duchy of a kingdom departs from the kingdom hue: [hue shift, saturation, lightness]. */
const DUCHY_TWEAK: [number, number, number][] = [[0, 0, 0], [-24, 0, 0.08], [22, 0.04, -0.08], [-44, -0.04, 0], [42, 0, 0.11], [-12, 0.04, -0.15], [14, 0, 0.16]];
interface KStyle { h: number; main: string; deep: string; soft: string }
const kStyle = (h: number): KStyle => ({ h, main: hsl(h, 0.66, 0.52), deep: hsl(h, 0.52, 0.27), soft: hsl(h, 0.5, 0.82) });

let measureCtx: CanvasRenderingContext2D | null | undefined;
/** Rendered width of a label in px; falls back to an estimate where canvas is unavailable. */
function textWidth(text: string, px: number, spacingEm = 0): number {
  if (measureCtx === undefined) {
    try { measureCtx = document.createElement('canvas').getContext('2d'); } catch { measureCtx = null; }
  }
  if (!measureCtx) return text.length * px * (0.7 + spacingEm);
  const fam = getComputedStyle(document.documentElement).getPropertyValue('--display').trim() || 'serif';
  measureCtx.font = `900 ${px}px ${fam}`;
  return measureCtx.measureText(text).width + text.length * px * spacingEm;
}
interface Banner { id: Id; name: string; seed: number | null; st: KStyle; w: number; h: number; mine: boolean }
const BANNER_FS = 17;
const BANNER_H = 38;

const TIER_SIZE = [1, 1.12, 1.3, 1.5];
/** Icon height in screen px: tracks the world scale so icons neither swamp a tight cluster nor vanish. */
const iconPx = (scale: number, tier: number, spacing = 60) => Math.max(14, Math.min(30, scale * spacing * 0.62)) * TIER_SIZE[tier];
const compact = (n: number) => (n >= 10000 ? `${Math.round(n / 1000)}k` : n >= 1000 ? `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k` : String(n));

function roadCurve(roadA: Id, roadB: Id, byId: Map<Id, TitleSummary>) {
  const [ida, idb] = roadA < roadB ? [roadA, roadB] : [roadB, roadA];
  const a = byId.get(ida)?.holding;
  const b = byId.get(idb)?.holding;
  if (!a || !b) return null;
  const c = roadControl(a, b, `${ida}|${idb}`);
  return { a, b, c, d: `M${a.x} ${a.y}Q${c.x.toFixed(1)} ${c.y.toFixed(1)} ${b.x} ${b.y}` };
}
/** Point at t on the road between the two ids, in either direction. */
function curvePoint(cv: { a: Pt; b: Pt; c: Pt }, t: number): Pt {
  const m = 1 - t;
  return { x: m * m * cv.a.x + 2 * m * t * cv.c.x + t * t * cv.b.x, y: m * m * cv.a.y + 2 * m * t * cv.c.y + t * t * cv.b.y };
}

/* ------------------------------------------------------------------ static art */
const MapDefs = memo(function MapDefs() {
  return (
    <defs>
      <radialGradient id="mk-sea" cx="50%" cy="50%" r="70%">
        <stop offset="0" stopColor="#1668c4" />
        <stop offset="0.55" stopColor="#0f4a9c" />
        <stop offset="1" stopColor="#0a2f6b" />
      </radialGradient>
      <pattern id="mk-waves" width="56" height="34" patternUnits="userSpaceOnUse">
        <path d="M4 10q5-5 10 0t10 0" fill="none" stroke="#8cc8ff" strokeWidth="1.6" strokeLinecap="round" opacity="0.28" />
        <path d="M32 27q5-5 10 0t10 0" fill="none" stroke="#8cc8ff" strokeWidth="1.6" strokeLinecap="round" opacity="0.2" />
        <circle cx="46" cy="8" r="1.1" fill="#bfe3ff" opacity="0.2" />
      </pattern>
      {/* terrain glyphs, drawn around the origin in ~10 unit boxes */}
      <g id="mk-g-tree" stroke={INK} strokeWidth="0.9" strokeLinejoin="round" strokeOpacity="0.55">
        <rect x="-0.8" y="2" width="1.6" height="2.6" fill="#6b4a2b" stroke="none" />
        <path d="M0,-5.4L4,0.4H-4Z" fill="#2f7f45" /><path d="M0,-2.6L4.6,3.2H-4.6Z" fill="#378f4d" />
      </g>
      <g id="mk-g-hill" stroke={INK} strokeWidth="0.9" strokeLinejoin="round" strokeOpacity="0.5">
        <path d="M-6,3.5Q-4,-3.5 0,-3.5T6,3.5Z" fill="#a08a49" /><path d="M-1,3.5Q1,-0.5 3.4,-0.5T8,3.5Z" fill="#b39d57" transform="translate(-1 0) scale(0.8)" />
      </g>
      <g id="mk-g-peak" strokeLinejoin="round">
        <path d="M-7,5L-1.5,-5.5L2,0.4L4.3,-2.6L8,5Z" fill="#8d96a6" stroke={INK} strokeWidth="0.9" strokeOpacity="0.55" />
        <path d="M-1.5,-5.5L-3.6,-1.4L-2,-2L-0.6,-0.6L0.6,-2L2,0.4Z" fill="#fff" opacity="0.92" />
      </g>
      <g id="mk-g-reed" fill="none" stroke="#2f7a6c" strokeWidth="1.2" strokeLinecap="round">
        <path d="M-3,3.5V-2M0,3.5V-4M3,3.5V-1.5" /><path d="M-5,4.5Q0,2.6 5,4.5" stroke="#3f9e90" opacity="0.8" />
      </g>
      <g id="mk-g-tuft" fill="none" stroke="#4e8f3b" strokeWidth="1.1" strokeLinecap="round" opacity="0.8"><path d="M-3,2.5L-3.6,-1M0,2.5V-2.5M3,2.5L3.6,-1" /></g>
      <g id="mk-g-field" opacity="0.8"><rect x="-5.5" y="-3.6" width="11" height="7.2" rx="1" fill="#e9de7d" stroke="#a89a38" strokeWidth="0.8" /><path d="M-3.4,-3.4V3.4M-0.4,-3.4V3.4M2.6,-3.4V3.4" stroke="#bdae48" strokeWidth="0.9" /></g>
      <g id="mk-g-shell" opacity="0.85"><path d="M-3.5,2.5Q-3.5,-2.5 0,-2.5T3.5,2.5Z" fill="#fbeedb" stroke="#c9a97a" strokeWidth="0.9" /></g>

      {/* settlement icons, ~26 units tall, base at y=9; accent colour comes from --acc */}
      <g id="mk-keep" strokeLinejoin="round" strokeLinecap="round">
        <path d="M0,-6V-16.5H7L5,-13.4L7,-10.2H0" fill="var(--acc)" stroke={INK} strokeWidth="1.5" />
        <path d="M-8,9V-3H-9.6V-9H-5.4V-6H-2V-9H2V-6H5.4V-9H9.6V-3H8V9Z" fill="#f3ecd8" stroke={INK} strokeWidth="2" />
        <path d="M-8,-0.4H8V2.4H-8Z" fill="var(--acc)" stroke={INK} strokeWidth="1.2" />
        <path d="M-2.7,9V5.2A2.7,2.7 0 0 1 2.7,5.2V9Z" fill={INK} />
        <path d="M-5.6,-5.2V-3.2M5.6,-5.2V-3.2" stroke={INK} strokeWidth="1.5" />
      </g>
      <g id="mk-city" strokeLinejoin="round" strokeLinecap="round">
        <path d="M-13,9V0.4H-5V9Z" fill="#f3ecd8" stroke={INK} strokeWidth="1.8" /><path d="M-14.2,0.6L-9,-5L-3.8,0.6Z" fill="var(--acc)" stroke={INK} strokeWidth="1.6" />
        <path d="M13,9V0.4H5V9Z" fill="#f3ecd8" stroke={INK} strokeWidth="1.8" /><path d="M14.2,0.6L9,-5L3.8,0.6Z" fill="var(--acc)" stroke={INK} strokeWidth="1.6" />
        <path d="M-4.8,9V-8H4.8V9Z" fill="#f7f1df" stroke={INK} strokeWidth="2" />
        <path d="M-6.6,-7.6L0,-17.5L6.6,-7.6Z" fill="var(--acc)" stroke={INK} strokeWidth="1.8" />
        <path d="M-1.9,9V4.6A1.9,1.9 0 0 1 1.9,4.6V9Z" fill={INK} />
        <path d="M0,-4.4V-1.8" stroke={INK} strokeWidth="1.6" /><path d="M-9,4V6.2M9,4V6.2" stroke={INK} strokeWidth="1.5" />
      </g>
      {/* unsettled frontier sites: hollow, dashed outlines of the keep and the city */}
      <g id="mk-site" strokeLinejoin="round" strokeLinecap="round" opacity="0.95">
        <path d="M-8,9V-3H-9.6V-9H-5.4V-6H-2V-9H2V-6H5.4V-9H9.6V-3H8V9Z" fill="rgba(255,255,255,0.1)" stroke={INK} strokeWidth="3.6" />
        <path d="M-8,9V-3H-9.6V-9H-5.4V-6H-2V-9H2V-6H5.4V-9H9.6V-3H8V9Z" fill="none" stroke="#fff" strokeWidth="1.6" strokeDasharray="3.2 2.6" />
      </g>
      <g id="mk-citysite" strokeLinejoin="round" strokeLinecap="round" opacity="0.95">
        <path d="M-13,9V0.4H-5V9M13,9V0.4H5V9M-4.8,9V-8L0,-17.5L4.8,-8V9Z" fill="rgba(255,255,255,0.1)" stroke={INK} strokeWidth="3.6" />
        <path d="M-13,9V0.4H-5V9M13,9V0.4H5V9M-4.8,9V-8L0,-17.5L4.8,-8V9Z" fill="none" stroke="#fff" strokeWidth="1.6" strokeDasharray="3.2 2.6" />
      </g>
      <g id="mk-crown" strokeLinejoin="round">
        <path d="M-6.5,-17.6L-7.6,-24.4L-3.4,-21L0,-26.4L3.4,-21L7.6,-24.4L6.5,-17.6Z" fill={GOLD} stroke={INK} strokeWidth="1.6" />
        <circle cx="0" cy="-22" r="1.1" fill="#e5483f" />
      </g>
      <g id="mk-star" strokeLinejoin="round"><path d="M0,-25L1.7,-21.2L5.8,-20.8L2.7,-18.2L3.7,-14.2L0,-16.3L-3.7,-14.2L-2.7,-18.2L-5.8,-20.8L-1.7,-21.2Z" fill={GOLD} stroke={INK} strokeWidth="1.3" /></g>
      <g id="mk-shield" strokeLinejoin="round" strokeLinecap="round">
        <path d="M-11.5,-14H11.5V3.2C11.5,10 5.6,14 0,16.4C-5.6,14 -11.5,10 -11.5,3.2Z" fill="var(--col)" stroke={INK} strokeWidth="2.6" />
        <path d="M-8,-10.8H8V3C8,8 4,11 0,12.8C-4,11 -8,8 -8,3Z" fill="#fff" opacity="0.22" />
        <path d="M-4.6,-5.4L4.6,4.6M4.6,-5.4L-4.6,4.6" stroke={INK} strokeWidth="2.4" opacity="0.8" />
        <path d="M-5.2,-14V-17.5M5.2,-14V-17.5" stroke={INK} strokeWidth="0" />
      </g>
    </defs>
  );
});

/* ------------------------------------------------------------------ land */
interface KGlow { n: number; color: string; deep: string; edges: string; cells: string }
interface LandProps {
  cells: LandCell[];
  fills: string[];
  glyphs: Glyph[] | null;
  borders: Map<string, string>;
  mineEdges: string;
  kGlow: KGlow[];
  focusEdges: string;
  mode: MapMode;
}
const LandLayer = memo(function LandLayer({ cells, fills, glyphs, borders, mineEdges, kGlow, focusEdges, mode }: LandProps) {
  const terr = mode === 'terrain';
  const coast = borders.get('coast') ?? '';
  const all = useMemo(() => cells.map((c) => c.path).join(''), [cells]);
  const kBorder = borders.get('k') ?? '';
  const gl = terr ? 0.82 : 1;
  return (
    <g className="land">
      <defs>
        {kGlow.map((k) => <clipPath key={k.n} id={`mk-kc-${k.n}`}><path d={k.cells} /></clipPath>)}
      </defs>
      {/* shallows (one path per band, so overlapping cells do not stack their alpha) and the cartoon "thickness" of the land */}
      <g fill="none" strokeLinejoin="round" style={{ pointerEvents: 'none' }}>
        <path d={all} stroke="#1f78d6" strokeOpacity="0.5" strokeWidth="46" />
        <path d={all} stroke="#3d9cec" strokeOpacity="0.55" strokeWidth="28" />
        <path d={all} stroke="#7cc5f6" strokeOpacity="0.6" strokeWidth="13" />
      </g>
      <path d={all} transform="translate(0 2.5)" fill="#0a2c63" stroke="#0a2c63" strokeWidth="3" strokeLinejoin="round" style={{ pointerEvents: 'none' }} />
      <g strokeLinejoin="round">
        {cells.map((c, i) => (
          <path key={c.id} d={c.path} fill={fills[i]} stroke={fills[i]} strokeWidth="0.8" vectorEffect="non-scaling-stroke" data-hid={c.id} />
        ))}
      </g>
      {glyphs && (
        <g style={{ pointerEvents: 'none' }}>
          {glyphs.map((g, i) => <use key={i} href={`#mk-g-${g.k}`} transform={`translate(${g.x} ${g.y}) scale(${(g.f ? -1 : 1) * g.s / 10} ${g.s / 10})`} />)}
        </g>
      )}
      {/* kingdom colour band: a soft glow hugging the inside of every kingdom's outline, clipped to its own land */}
      <g fill="none" strokeLinejoin="round" strokeLinecap="round" style={{ pointerEvents: 'none' }}>
        {kGlow.map((k) => (
          <g key={k.n} clipPath={`url(#mk-kc-${k.n})`}>
            <path d={k.edges} stroke={k.deep} strokeOpacity={0.22 * gl} strokeWidth="46" vectorEffect="non-scaling-stroke" />
            <path d={k.edges} stroke={k.deep} strokeOpacity={0.3 * gl} strokeWidth="28" vectorEffect="non-scaling-stroke" />
            <path d={k.edges} stroke={k.deep} strokeOpacity={0.95} strokeWidth="12" vectorEffect="non-scaling-stroke" />
            <path d={k.edges} stroke={k.color} strokeOpacity={1} strokeWidth="5" vectorEffect="non-scaling-stroke" />
          </g>
        ))}
      </g>
      <g fill="none" strokeLinejoin="round" strokeLinecap="round" style={{ pointerEvents: 'none' }}>
        <path d={borders.get('b') ?? ''} stroke={INK} strokeOpacity={terr ? 0.18 : 0.22} strokeWidth="1" vectorEffect="non-scaling-stroke" />
        <path d={borders.get('c') ?? ''} stroke={INK} strokeOpacity={terr ? 0.3 : 0.4} strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
        <path d={borders.get('d') ?? ''} stroke={INK} strokeOpacity={0.8} strokeWidth="3.4" vectorEffect="non-scaling-stroke" />
        <path d={borders.get('d') ?? ''} stroke="#fff" strokeOpacity={0.35} strokeWidth="1" vectorEffect="non-scaling-stroke" />
        {/* kingdom border: dark outline, pale core, dashed "march" line */}
        <path d={kBorder} stroke={INK} strokeWidth="8" vectorEffect="non-scaling-stroke" />
        <path d={kBorder} stroke="#fff" strokeWidth="4" vectorEffect="non-scaling-stroke" />
        <path d={kBorder} stroke={INK} strokeWidth="1.8" strokeDasharray="7 6" strokeLinecap="butt" vectorEffect="non-scaling-stroke" />
        {/* coastline */}
        <path d={coast} stroke={INK} strokeWidth="4" vectorEffect="non-scaling-stroke" />
        <path d={coast} stroke="#f6ecc4" strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
        {mineEdges && <path d={mineEdges} stroke={INK} strokeOpacity={0.85} strokeWidth="8" vectorEffect="non-scaling-stroke" />}
        {mineEdges && <path d={mineEdges} stroke={GOLD} strokeOpacity={0.5} strokeWidth="10" vectorEffect="non-scaling-stroke" />}
        {mineEdges && <path d={mineEdges} stroke={GOLD} strokeWidth="4" vectorEffect="non-scaling-stroke" />}
        {focusEdges && (
          <g className="focus-ring">
            <path d={focusEdges} stroke={INK} strokeOpacity={0.8} strokeWidth="9" vectorEffect="non-scaling-stroke" />
            <path d={focusEdges} stroke="#fff" strokeWidth="4.5" strokeDasharray="9 6" vectorEffect="non-scaling-stroke" />
          </g>
        )}
      </g>
    </g>
  );
});

/* ------------------------------------------------------------------ roads */
const RoadsLayer = memo(function RoadsLayer({ roads, byId, u }: { roads: Road[]; byId: Map<Id, TitleSummary>; u: number }) {
  const ds = useMemo(() => roads.map((r) => roadCurve(r.a, r.b, byId)?.d).filter(Boolean) as string[], [roads, byId]);
  const all = ds.join('');
  return (
    <g fill="none" strokeLinecap="round" style={{ pointerEvents: 'none' }}>
      <path d={all} stroke="#5a3d1c" strokeOpacity={0.45} strokeWidth={5.4 * u} />
      <path d={all} stroke="#f3e0b0" strokeWidth={3 * u} strokeDasharray={`${0.1 * u} ${5.2 * u}`} />
    </g>
  );
});

/* ------------------------------------------------------------------ settlements */
interface HoldProps {
  items: Item[]; u: number; scale: number; spacing: number; labels: Set<Id>; selId: Id | null;
  targets: Map<Id, number> | null; showDays: boolean;
}
const HoldingsLayer = memo(function HoldingsLayer({ items, u, scale, spacing, labels, selId, targets, showDays }: HoldProps) {
  return (
    <g>
      {items.map((it) => {
        const px = iconPx(scale, it.tier, spacing);
        const S = (px * u) / 26;
        const sel = it.id === selId;
        const days = targets?.get(it.id);
        const isTarget = days !== undefined && days > 0;
        const ringR = px * 0.78;
        return (
          <g key={it.id} transform={`translate(${it.x} ${it.y})`} data-hid={it.id} style={{ cursor: 'pointer' }}>
            <circle r={Math.max(15, px * 0.7) * u} fill="transparent" data-hid={it.id} />
            <g transform={`scale(${u})`}>
              {it.mine && <circle r={ringR} cy={-px * 0.06} fill={GOLD} opacity={0.42} />}
              {it.mine && <circle r={ringR} cy={-px * 0.06} fill="none" stroke={INK} strokeWidth={6} />}
              {it.mine && <circle r={ringR} cy={-px * 0.06} fill="none" stroke={GOLD} strokeWidth={3.4} />}
              {it.siege && <circle className="siege-ring" r={ringR + 4} cy={-px * 0.06} fill="none" stroke={ENEMY} strokeWidth={2.6} strokeDasharray="5 4" />}
              {isTarget && <circle className="pulse-ring" r={ringR + 3} cy={-px * 0.06} fill="rgba(255,212,107,0.16)" stroke={GOLD} strokeWidth={2} strokeDasharray="3 3" />}
              {sel && <circle className="pulse-ring sel" r={ringR + 5} cy={-px * 0.06} fill="none" stroke="#fff" strokeWidth={3} />}
              {sel && <circle r={ringR + 5} cy={-px * 0.06} fill="none" stroke={GOLD} strokeWidth={1.6} />}
            </g>
            <g transform={`scale(${S})`}>
              <ellipse cx="0" cy="9.4" rx="12.5" ry="3.6" fill={INK} opacity="0.4" />
              <use href={`#mk-${it.kind}`} style={{ ['--acc' as string]: it.accent }} />
              {it.tier >= 3 && <use href="#mk-crown" />}
              {it.tier === 2 && <use href="#mk-star" />}
            </g>
            {labels.has(it.id) && (
              <g transform={`scale(${u})`}>
                <text className="mk-label" textAnchor="middle" y={px * 0.42 + 13} fontSize={12}>{it.name}</text>
              </g>
            )}
            {isTarget && showDays && (
              <g transform={`scale(${u})`} className="mk-days">
                <rect x={-14} y={-px * 0.5 - 20} width={28} height={15} rx={7.5} fill={INK} stroke={GOLD} strokeWidth={1.6} />
                <text textAnchor="middle" y={-px * 0.5 - 8.6} fontSize={10} fill={GOLD} fontWeight={900}>{days}d</text>
              </g>
            )}
          </g>
        );
      })}
    </g>
  );
});

/* ------------------------------------------------------------------ armies */
interface ArmiesProps {
  armyAt: Map<Id, ArmyView[]>; byId: Map<Id, TitleSummary>; items: Map<Id, Item>; u: number; scale: number; spacing: number;
  myHouseId: Id | undefined; hostile: Set<Id>; selectedArmyId: Id | null; panelArmy: Id | null; showCount: boolean;
}
const ArmiesLayer = memo(function ArmiesLayer({ armyAt, byId, items, u, scale, spacing, myHouseId, hostile, selectedArmyId, panelArmy, showCount }: ArmiesProps) {
  return (
    <g>
      {[...armyAt.entries()].map(([locId, list]) => {
        const hd = byId.get(locId)?.holding;
        const it = items.get(locId);
        if (!hd) return null;
        const lift = (it ? iconPx(scale, it.tier, spacing) : 20) * 0.62 + 24;
        return list.map((a, i) => {
          const own = a.houseId === myHouseId;
          const enemy = !own && hostile.has(a.ownerId);
          const col = own ? GOLD : enemy ? ENEMY : NEUTRAL;
          const sel = a.id === selectedArmyId || a.id === panelArmy;
          const offX = (i - (list.length - 1) / 2) * 34;
          return (
            <g key={a.id} transform={`translate(${hd.x} ${hd.y}) scale(${u}) translate(${offX} ${-lift})`} data-aid={a.id} style={{ cursor: 'pointer' }}>
              <circle r={22} cy={2} fill="transparent" data-aid={a.id} />
              {sel && <circle className="pulse-ring sel" r={25} cy={1} fill="rgba(255,212,107,0.2)" stroke={GOLD} strokeWidth={3} />}
              <ellipse cy={17} rx={11} ry={3} fill={INK} opacity={0.35} />
              <use href="#mk-shield" style={{ ['--col' as string]: col }} />
              {showCount && (
                <g>
                  <rect x={-17} y={13} width={34} height={16} rx={8} fill={INK} stroke={col} strokeWidth={2} />
                  <text className="mk-army-n" textAnchor="middle" y={25} fontSize={11.5}>{compact(a.size)}</text>
                </g>
              )}
            </g>
          );
        });
      })}
    </g>
  );
});

/* ------------------------------------------------------------------ kingdom banner */
/** A ribbon with the ruling house's crest, a crown and the kingdom name, drawn in screen px around its own origin. */
const KingdomBanner = memo(function KingdomBanner({ b }: { b: Banner }) {
  const { w, h, st } = b;
  const x0 = -w / 2;
  const y0 = -h / 2;
  const tail = shade(st.deep, -0.3);
  const textX = x0 + 10 + (b.seed !== null ? 28 : 0) + 24 + (w - 10 - (b.seed !== null ? 28 : 0) - 24 - 14) / 2;
  return (
    <g className="kbanner">
      <ellipse cx={0} cy={h / 2 + 8} rx={w / 2 + 6} ry={4} fill={INK} opacity={0.28} />
      <path d={`M${x0 - 15} ${y0 + 9}H${x0 + 10}V${y0 + h + 9}H${x0 - 15}L${x0 - 7} ${y0 + h / 2 + 9}Z`} fill={tail} stroke={INK} strokeWidth={2.6} strokeLinejoin="round" />
      <path d={`M${-x0 + 15} ${y0 + 9}H${-x0 - 10}V${y0 + h + 9}H${-x0 + 15}L${-x0 + 7} ${y0 + h / 2 + 9}Z`} fill={tail} stroke={INK} strokeWidth={2.6} strokeLinejoin="round" />
      <rect x={x0} y={y0} width={w} height={h} rx={6} fill={st.deep} stroke={INK} strokeWidth={3.4} />
      <rect x={x0 + 1.5} y={y0 + 1.5} width={w - 3} height={h / 2 - 1} rx={5} fill="#fff" opacity={0.14} />
      <rect x={x0 + 3.5} y={y0 + 3.5} width={w - 7} height={h - 7} rx={4} fill="none" stroke={b.mine ? GOLD : st.soft} strokeOpacity={0.9} strokeWidth={1.4} />
      {b.seed !== null && (
        <g transform={`translate(${x0 + 10} ${-15})`}>
          <rect x={-2} y={-2} width={28} height={34} rx={4} fill={INK} opacity={0.55} />
          <Heraldry seed={b.seed} size={24} />
        </g>
      )}
      <g transform={`translate(${x0 + 10 + (b.seed !== null ? 28 : 0) + 11} 0) scale(0.92)`}>
        <use href="#mk-crown" transform="translate(0 22)" />
      </g>
      <text className="kbanner-name" x={textX} y={BANNER_FS * 0.36} textAnchor="middle" fontSize={BANNER_FS}>{b.name}</text>
    </g>
  );
});

/* ------------------------------------------------------------------ "You" marker */
interface YouProps { x: number; y: number; tipY: number; u: number; seed: number; label: string }
/** A chunky gold pin carrying the house heraldry. Purely visual: taps fall through to what lies underneath. */
const YouMarker = memo(function YouMarker({ x, y, tipY, u, seed, label }: YouProps) {
  return (
    <g transform={`translate(${x} ${y}) scale(${u * 1.2}) translate(0 ${tipY / 1.2})`} style={{ pointerEvents: 'none' }} className="you-marker" aria-label={label}>
      <ellipse className="you-halo" cx={0} cy={0} rx={24} ry={9} fill="rgba(255,212,107,0.4)" stroke={GOLD} strokeWidth={2.5} />
      <ellipse cx={0} cy={0} rx={5} ry={2} fill={INK} opacity={0.5} />
      <g className="you-bob">
        <path d="M0,0C-6,-9 -23,-21 -23,-35A23,23 0 0 1 23,-35C23,-21 6,-9 0,0Z" fill={GOLD} stroke={INK} strokeWidth={4.2} strokeLinejoin="round" />
        <path d="M-17,-37A17,17 0 0 1 -5,-50" fill="none" stroke="#fff" strokeOpacity={0.7} strokeWidth={3} strokeLinecap="round" />
        <circle cx={0} cy={-35} r={15.5} fill="#fff7dc" stroke={INK} strokeWidth={3} />
        <g transform="translate(-11 -48.6)"><Heraldry seed={seed} size={22} /></g>
        <g transform="translate(0 -62)">
          <rect x={-18} y={-9} width={36} height={18} rx={9} fill={INK} stroke={GOLD} strokeWidth={2.2} />
          <text className="you-label" textAnchor="middle" y={4.6} fontSize={13}>{label}</text>
        </g>
      </g>
    </g>
  );
});

export function MapScreen() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const setSubview = (v: 'world' | 'military') => setParams(v === 'world' ? {} : { view: v }, { replace: true });
  const worldView = params.get('view') !== 'military';
  const world = useWorld();
  const house = useHouse();
  const mapMode = useUi((s) => s.mapMode);
  const setMapMode = useUi((s) => s.setMapMode);
  const selectedArmyId = useUi((s) => s.selectedArmyId);
  const selectArmy = useUi((s) => s.selectArmy);

  const wrapRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 390, h: 560 });
  const [view, setView] = useState<View | null>(null);
  const [panel, setPanel] = useState<Panel>(null);
  const [legend, setLegend] = useState(false);
  const [realms, setRealms] = useState(false);
  const [focusId, setFocusId] = useState<Id | null>(null);
  const [fontTick, setFontTick] = useState(0);

  const w = world.data;
  const myHouseId = house.data?.house.id;

  const byId = useMemo(() => new Map<Id, TitleSummary>((w?.titles ?? []).map((x) => [x.id, x])), [w]);
  const holdings = useMemo(() => (w?.titles ?? []).filter((x) => x.holding), [w]);

  // ---- geometry (memoised on the title set)
  const land = useMemo(() => buildLand(holdings.map((h) => ({ id: h.id, x: h.holding!.x, y: h.holding!.y }))), [holdings]);
  const bounds = useMemo(() => {
    const b = land.bbox;
    const pad = 28;
    return { x0: b.x0 - pad, y0: b.y0 - pad, x1: b.x1 + pad, y1: b.y1 + pad };
  }, [land]);
  const lineage = useMemo(() => holdings.map((h) => {
    const county = ancestorOfRank(h, 'county', byId);
    const duchy = ancestorOfRank(h, 'duchy', byId);
    const kingdom = ancestorOfRank(h, 'kingdom', byId);
    return { county: county?.id ?? h.id, duchy: duchy?.id ?? county?.id ?? h.id, kingdom: kingdom?.id ?? duchy?.id ?? 'x', color: duchy?.color ?? h.color };
  }), [holdings, byId]);
  const mineFlags = useMemo(() => holdings.map((h) => !!myHouseId && h.holderHouseId === myHouseId), [holdings, myHouseId]);

  // ---- kingdom identity: one hue each, duchies are shades of it
  const kStyles = useMemo(() => {
    const ids = [...(w?.kingdomIds ?? [])];
    for (const l of lineage) if (!ids.includes(l.kingdom)) ids.push(l.kingdom);
    return new Map<Id, KStyle>(ids.map((id, i) => [id, kStyle(KINGDOM_HUES[i % KINGDOM_HUES.length])]));
  }, [w, lineage]);
  const kingdomColor = (id: Id) => (kStyles.get(id) ?? kStyle(KINGDOM_HUES[0])).main;
  const duchyTweak = useMemo(() => {
    const per = new Map<Id, Id[]>();
    for (const l of lineage) {
      const list = per.get(l.kingdom) ?? [];
      if (!list.includes(l.duchy)) list.push(l.duchy);
      per.set(l.kingdom, list);
    }
    const out = new Map<Id, [number, number, number]>();
    for (const list of per.values()) [...list].sort().forEach((d, i) => out.set(d, DUCHY_TWEAK[i % DUCHY_TWEAK.length]));
    return out;
  }, [lineage]);
  const kGlow = useMemo((): KGlow[] => {
    const edges = groupEdges(land.cells, (i) => lineage[i].kingdom);
    const cellPaths = new Map<Id, string[]>();
    for (const c of land.cells) { const k = lineage[c.idx].kingdom; (cellPaths.get(k) ?? cellPaths.set(k, []).get(k)!).push(c.path); }
    return [...edges.keys()].map((id, n) => ({ n, color: (kStyles.get(id) ?? kStyle(KINGDOM_HUES[0])).main, deep: (kStyles.get(id) ?? kStyle(KINGDOM_HUES[0])).deep, edges: edges.get(id) ?? '', cells: (cellPaths.get(id) ?? []).join('') }));
  }, [land, lineage, kStyles]);
  const focusEdges = useMemo(() => {
    if (!focusId) return '';
    const inside = new Set<number>();
    holdings.forEach((h, i) => {
      for (let c: TitleSummary | undefined = h, g = 0; c && g < 8; g++) { if (c.id === focusId) { inside.add(i); break; } c = c.deJureParentId ? byId.get(c.deJureParentId) : undefined; }
    });
    return groupEdges(land.cells, (i) => (inside.has(i) ? 'in' : 'out')).get('in') ?? '';
  }, [focusId, holdings, byId, land]);
  const borders = useMemo(() => edgePaths(land.cells, (i, j) => {
    if (j === -1) return 'coast';
    const a = lineage[i];
    const b = lineage[j];
    return a.kingdom !== b.kingdom ? 'k' : a.duchy !== b.duchy ? 'd' : a.county !== b.county ? 'c' : 'b';
  }), [land, lineage]);
  const mineEdges = useMemo(() => {
    if (!mineFlags.some(Boolean)) return '';
    return edgePaths(land.cells, (i, j) => (j === -1 ? (mineFlags[i] ? 'm' : null) : mineFlags[i] !== mineFlags[j] ? 'm' : null)).get('m') ?? '';
  }, [land, mineFlags]);
  const hueOf = (i: number): { h: number; dl: number; ds: number } => {
    const l = lineage[i];
    const [dh, ds, dl] = duchyTweak.get(l.duchy) ?? [0, 0, 0];
    return { h: (kStyles.get(l.kingdom)?.h ?? KINGDOM_HUES[0]) + dh, ds, dl };
  };
  const fills = useMemo(() => land.cells.map((c) => {
    const hd = holdings[c.idx].holding!;
    if (mapMode === 'terrain') return shade(TERRAIN_FILL[hd.terrain] ?? '#8fb35a', (h01(c.id) - 0.5) * 0.1);
    const kc = h01(lineage[c.idx].county) - 0.5;
    const { h, ds, dl } = hueOf(c.idx);
    const base = hsl(h + kc * 7, 0.5 + ds, 0.54 + dl + kc * 0.05);
    return mapMode === 'armies' ? mix(base, '#7a8aa8', 0.55) : base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [land, holdings, lineage, mapMode, kStyles, duchyTweak]);
  const glyphs = useMemo(
    () => (mapMode === 'terrain' ? terrainGlyphs(land.cells, (c) => holdings[c.idx].holding!.terrain) : null),
    [land, holdings, mapMode],
  );

  const items = useMemo(() => {
    const kingCap = new Set<Id>();
    const duchyCap = new Set<Id>();
    const countyCap = new Set<Id>();
    for (const x of w?.titles ?? []) {
      if (!x.capitalId) continue;
      if (x.rank === 'kingdom') kingCap.add(x.capitalId);
      else if (x.rank === 'duchy') duchyCap.add(x.capitalId);
      else if (x.rank === 'county') countyCap.add(x.capitalId);
    }
    const besieged = new Set<Id>((w?.armies ?? []).map((a) => a.siege?.holdingId).filter(Boolean) as Id[]);
    return holdings.map((h, i): Item => {
      const hd = h.holding!;
      const tier = kingCap.has(h.id) ? 3 : duchyCap.has(h.id) ? 2 : countyCap.has(h.id) || h.rank === 'city' ? 1 : 0;
      return {
        id: h.id, name: h.name, x: hd.x, y: hd.y, terrain: hd.terrain, tier,
        kind: h.frontier?.settled === false ? (h.rank === 'city' ? 'citysite' : 'site') : h.rank === 'city' ? 'city' : 'keep',
        mine: mineFlags[i], accent: (() => { const { h, ds, dl } = hueOf(i); return hsl(h, 0.6 + ds, 0.46 + dl * 0.6); })(), siege: besieged.has(h.id) || !!hd.occupiedBy, dev: hd.development,
      };
    }).sort((a, b) => a.y - b.y);
  }, [holdings, w, mineFlags, lineage, kStyles, duchyTweak]);
  const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);

  const regions = useMemo(() => {
    const acc = new Map<Id, { x: number; y: number; n: number; x0: number; x1: number; y0: number; y1: number }>();
    const add = (id: Id, x: number, y: number) => {
      const a = acc.get(id) ?? { x: 0, y: 0, n: 0, x0: Infinity, x1: -Infinity, y0: Infinity, y1: -Infinity };
      a.x += x; a.y += y; a.n++; a.x0 = Math.min(a.x0, x); a.x1 = Math.max(a.x1, x); a.y0 = Math.min(a.y0, y); a.y1 = Math.max(a.y1, y); acc.set(id, a);
    };
    holdings.forEach((h, i) => { add(lineage[i].duchy, h.holding!.x, h.holding!.y); add(lineage[i].kingdom, h.holding!.x, h.holding!.y); });
    const out: { id: Id; name: string; x: number; y: number; w: number; rank: 'kingdom' | 'duchy' }[] = [];
    const regionNo = new Map<Id, number>((w?.frontier ?? []).map((r, i) => [r.kingdomId, i + 1]));
    for (const [id, a] of acc) {
      const t0 = byId.get(id);
      if (!t0 || (t0.rank !== 'duchy' && t0.rank !== 'kingdom')) continue;
      let name = t0.name.replace(/^(Kingdom|Duchy|County) of /, '');
      if (t0.frontier?.uncreated) {
        // an uncreated frontier realm is just "Frontier" on the map (its duchies carry no label)
        if (t0.rank === 'duchy') continue;
        name = (w?.frontier?.length ?? 0) > 1 ? `${t('frontier.mapLabel')} ${regionNo.get(id) ?? ''}`.trim() : t('frontier.mapLabel');
      }
      out.push({ id, name, x: a.x / a.n, y: a.y / a.n, w: a.x1 - a.x0 + land.spacing * 1.2, rank: t0.rank });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holdings, lineage, byId, land.spacing, w?.frontier, t]);
  const multiKingdom = regions.filter((r) => r.rank === 'kingdom').length > 1;
  useEffect(() => { void document.fonts?.ready.then(() => setFontTick(1)); }, []);
  const banners = useMemo(() => {
    const out = new Map<Id, Banner>();
    for (const r of regions) {
      if (r.rank !== 'kingdom') continue;
      const k = byId.get(r.id);
      const seed = (w?.houses ?? []).find((x) => x.id === k?.holderHouseId)?.heraldrySeed ?? null;
      const name = r.name.toUpperCase();
      const tw = textWidth(name, BANNER_FS, 0.06) + 6;
      out.set(r.id, { id: r.id, name, seed, st: kStyles.get(r.id) ?? kStyle(KINGDOM_HUES[0]), w: Math.round(10 + (seed !== null ? 28 : 0) + 24 + tw + 14), h: BANNER_H, mine: !!myHouseId && k?.holderHouseId === myHouseId });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regions, byId, w, kStyles, myHouseId, fontTick]);

  const armyAt = useMemo(() => {
    const m = new Map<Id, ArmyView[]>();
    for (const a of w?.armies ?? []) {
      if (!m.has(a.locationId)) m.set(a.locationId, []);
      m.get(a.locationId)!.push(a);
    }
    return m;
  }, [w]);
  const hostile = useMemo(() => (w ? hostileOwners(w, house.data?.wars ?? [], memberIds(house.data)) : new Set<Id>()), [w, house.data]);

  // ---- where the player's character stands: the head of house, or the army they march with
  const me = useMemo(() => {
    const hv = house.data;
    if (!hv) return null;
    const head = hv.members.find((m) => m.id === hv.house.headId) ?? null;
    if (!head) return null;
    const cmd = head.commandingArmyId ? (w?.armies ?? []).find((a) => a.id === head.commandingArmyId) : undefined;
    const locId = cmd?.locationId ?? head.locationId;
    const hd = byId.get(locId)?.holding;
    if (!hd) return null;
    const list = armyAt.get(locId) ?? [];
    const idx = cmd ? list.findIndex((a) => a.id === cmd.id) : -1;
    return { locId, x: hd.x, y: hd.y, idx, n: list.length, armyCount: list.length, seed: hv.house.heraldrySeed, placeName: byId.get(locId)?.name ?? '' };
  }, [house.data, w, byId, armyAt]);

  const myArmies = useMemo(() => (house.data?.armies ?? []), [house.data]);
  const selArmy = myArmies.find((a) => a.id === selectedArmyId) ?? null;
  const targets = useMemo(() => (selArmy && w ? daysFrom(w.roads, selArmy.locationId) : null), [selArmy, w]);

  // ---- view
  const bw = bounds.x1 - bounds.x0;
  const bh = bounds.y1 - bounds.y0;
  const s0 = Math.min((size.w - 16) / bw, (size.h - TOP_INSET - BOTTOM_INSET) / bh);
  const v: View = view ?? { cx: (bounds.x0 + bounds.x1) / 2, cy: (bounds.y0 + bounds.y1) / 2 - (TOP_INSET - BOTTOM_INSET) / 2 / s0, zoom: 1 };
  const scale = s0 * v.zoom;
  const u = 1 / scale; // user units per screen pixel
  const vw = size.w / scale;
  const vh = size.h / scale;

  // Latest geometry for the (non-React) wheel listener and pointer math.
  const geom = useRef({ v, s0, size, bounds });
  geom.current = { v, s0, size, bounds };

  // Quantise so the heavy layers do not re-render for every pixel of a pinch.
  const qScale = Math.round(scale * 40) / 40;
  const qU = 1 / qScale;

  const selHoldingId = panel?.kind === 'holding' ? panel.id : null;
  const labels = useMemo(() => {
    const boxes: [number, number, number, number][] = [];
    const hit = (b: [number, number, number, number]) => boxes.some((o) => b[0] < o[2] && b[2] > o[0] && b[1] < o[3] && b[3] > o[1]);
    for (const it of items) {
      const px = iconPx(qScale, it.tier, land.spacing);
      boxes.push([it.x * qScale - px * 0.55, it.y * qScale - px * 0.75, it.x * qScale + px * 0.55, it.y * qScale + px * 0.4]);
    }
    const order = [...items].sort((a, b) => score(b, selHoldingId) - score(a, selHoldingId));
    const out = new Set<Id>();
    const placedLabels: [number, number, number, number][] = [];
    for (const it of order) {
      const px = iconPx(qScale, it.tier, land.spacing);
      const wdt = it.name.length * 7.4 + 8;
      const top = it.y * qScale + px * 0.42 + 1;
      const b: [number, number, number, number] = [it.x * qScale - wdt / 2, top, it.x * qScale + wdt / 2, top + 15];
      if (it.id === selHoldingId || !hit(b)) { out.add(it.id); boxes.push(b); placedLabels.push(b); }
    }
    return { ids: out, boxes };
  }, [items, qScale, selHoldingId, land.spacing]);

  // Kingdom banners are placed once, against the settlements at the fitted scale, so they never jump while zooming.
  // Each is sized to its kingdom (small realms get a smaller ribbon) and kept inside the kingdom's own extent.
  const bannerPos = useMemo(() => {
    const res = new Map<Id, Pt & { k0: number }>();
    const boxes: [number, number, number, number][] = [];
    for (const it of items) {
      const px = iconPx(s0, it.tier, land.spacing);
      boxes.push([it.x * s0 - px * 0.6, it.y * s0 - px * 0.8, it.x * s0 + px * 0.6, it.y * s0 + px * 0.9]);
      const lw = it.name.length * 7.4 + 8;
      boxes.push([it.x * s0 - lw / 2, it.y * s0 + px * 0.4, it.x * s0 + lw / 2, it.y * s0 + px * 0.4 + 16]);
    }
    for (const locId of armyAt.keys()) {
      const hd = byId.get(locId)?.holding;
      if (hd) boxes.push([hd.x * s0 - 24, hd.y * s0 - 70, hd.x * s0 + 24, hd.y * s0 - 14]);
    }
    if (me) boxes.push([me.x * s0 - 30, me.y * s0 - 130, me.x * s0 + 30, me.y * s0]);
    for (const r of regions) {
      const ban = banners.get(r.id);
      if (!ban) continue;
      const k0 = Math.max(0.5, Math.min(1, (r.w * s0) / (ban.w * 1.05)));
      const wdt = (ban.w + 30) * k0;
      const hgt = (ban.h + 14) * k0;
      const dxMax = Math.max(0, (r.w * s0 - wdt) / 2);
      const xMin = bounds.x0 * s0 + wdt / 2 + 6;
      const xMax = bounds.x1 * s0 - wdt / 2 - (size.w < 640 ? 58 : 8);
      let best: Pt | null = null;
      let bestCost = Infinity;
      for (let dy = -230; dy <= 230; dy += 8) {
        for (let dx = -dxMax; dx <= dxMax + 0.1; dx += Math.max(6, dxMax / 8)) {
          const cx = r.x * s0 + dx;
          const cy = r.y * s0 + dy;
          if (xMax > xMin && (cx < xMin || cx > xMax)) continue;
          let hits = 0;
          for (const o of boxes) if (cx - wdt / 2 < o[2] && cx + wdt / 2 > o[0] && cy - hgt / 2 < o[3] && cy + hgt / 2 > o[1]) hits++;
          let near = Infinity;
          for (const it of items) near = Math.min(near, Math.hypot(it.x * s0 - cx, it.y * s0 - cy));
          const cost = hits * 700 + Math.hypot(dx, dy * 1.4) + (near > land.radius * s0 * 1.1 ? 450 : 0);
          if (cost < bestCost) { bestCost = cost; best = { x: cx, y: cy }; }
        }
      }
      const p = best ?? { x: r.x * s0, y: r.y * s0 };
      res.set(r.id, { x: p.x / s0, y: p.y / s0, k0 });
      boxes.push([p.x - wdt / 2, p.y - hgt / 2, p.x + wdt / 2, p.y + hgt / 2]);
    }
    return res;
  }, [regions, banners, items, s0, land.spacing, land.radius, armyAt, byId, me, bounds, size.w]);

  // Duchy names go where they collide least with settlements, their labels and the banners.
  const regionPos = useMemo(() => {
    const boxes = [...labels.boxes];
    for (const [id, p] of bannerPos) {
      const ban = banners.get(id)!;
      boxes.push([p.x * qScale - (ban.w / 2 + 15) * p.k0, p.y * qScale - (ban.h / 2 + 7) * p.k0, p.x * qScale + (ban.w / 2 + 15) * p.k0, p.y * qScale + (ban.h / 2 + 7) * p.k0]);
    }
    const res = new Map<Id, Pt>();
    for (const r of regions) {
      if (r.rank === 'kingdom') continue;
      const px = 13;
      const wdt = r.name.length * px * (0.62 + 0.26);
      const hgt = px * 1.15;
      let best: Pt | null = null;
      let bestCost = Infinity;
      for (let dy = -150; dy <= 150; dy += 10) {
        for (let dx = -150; dx <= 150; dx += 14) {
          const cx = r.x * qScale + dx;
          const cy = r.y * qScale + dy;
          const b: [number, number, number, number] = [cx - wdt / 2, cy - hgt / 2, cx + wdt / 2, cy + hgt / 2];
          let hits = 0;
          for (const o of boxes) if (b[0] < o[2] && b[2] > o[0] && b[1] < o[3] && b[3] > o[1]) hits++;
          // stay over land: some holding must sit near the label centre
          let near = Infinity;
          for (const it of items) near = Math.min(near, Math.hypot(it.x * qScale - cx, it.y * qScale - cy));
          const cost = hits * 1000 + Math.hypot(dx, dy) + (near > land.radius * qScale * 1.1 ? 600 : 0);
          if (cost < bestCost) { bestCost = cost; best = { x: cx - wdt / 2, y: cy }; }
        }
      }
      if (best && bestCost < 1000) {
        res.set(r.id, { x: (best.x + wdt / 2) / qScale, y: best.y / qScale });
        boxes.push([best.x, best.y - hgt / 2, best.x + wdt, best.y + hgt / 2]);
      }
    }
    return res;
  }, [regions, banners, bannerPos, labels, items, qScale, land.radius]);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => setSize({ w: Math.max(200, el.clientWidth), h: Math.max(200, el.clientHeight) });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [w ? 1 : 0, worldView]);

  const clampView = (nv: View): View => {
    const b = geom.current.bounds;
    return {
      zoom: Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, nv.zoom)),
      cx: Math.max(b.x0, Math.min(b.x1, nv.cx)),
      cy: Math.max(b.y0, Math.min(b.y1, nv.cy)),
    };
  };
  const zoomAt = (px: number, py: number, newZoom: number) => {
    const g = geom.current;
    const sc = g.s0 * g.v.zoom;
    const wx = g.v.cx + (px - g.size.w / 2) / sc;
    const wy = g.v.cy + (py - g.size.h / 2) / sc;
    const nz = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));
    const nsc = g.s0 * nz;
    setView(clampView({ zoom: nz, cx: wx - (px - g.size.w / 2) / nsc, cy: wy - (py - g.size.h / 2) / nsc }));
  };
  const zoomAtRef = useRef(zoomAt);
  zoomAtRef.current = zoomAt;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if ((e.target as Element).closest?.('.map-sheet, .map-panel, .map-legend')) return; // let panels scroll
      e.preventDefault();
      const r = el.getBoundingClientRect();
      zoomAtRef.current(e.clientX - r.left, e.clientY - r.top, geom.current.v.zoom * Math.exp(-e.deltaY * 0.0018));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [w ? 1 : 0, worldView]);

  // ---- pointer gestures: one finger pans, two fingers pinch, a short press is a tap.
  const pts = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<{ sx: number; sy: number; cx: number; cy: number; moved: boolean; hid: string | null; aid: string | null } | null>(null);
  const pinch = useRef<{ d: number; zoom: number } | null>(null);

  const local = (e: RPointerEvent) => {
    const r = wrapRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const onDown = (e: RPointerEvent) => {
    if ((e.target as Element).closest('.map-ui')) return;
    const p = local(e);
    pts.current.set(e.pointerId, p);
    if (pts.current.size === 1) {
      const el = (e.target as Element).closest('[data-hid],[data-aid]');
      gesture.current = { sx: p.x, sy: p.y, cx: geom.current.v.cx, cy: geom.current.v.cy, moved: false, hid: el?.getAttribute('data-hid') ?? null, aid: el?.getAttribute('data-aid') ?? null };
    } else if (pts.current.size === 2) {
      const [a, b] = [...pts.current.values()];
      pinch.current = { d: Math.hypot(a.x - b.x, a.y - b.y) || 1, zoom: geom.current.v.zoom };
      if (gesture.current) gesture.current.moved = true;
    }
  };
  const onMove = (e: RPointerEvent) => {
    if (!pts.current.has(e.pointerId)) return;
    const p = local(e);
    pts.current.set(e.pointerId, p);
    if (pts.current.size >= 2 && pinch.current) {
      const [a, b] = [...pts.current.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      zoomAt((a.x + b.x) / 2, (a.y + b.y) / 2, pinch.current.zoom * (d / pinch.current.d));
    } else if (gesture.current) {
      const g = gesture.current;
      const dx = p.x - g.sx;
      const dy = p.y - g.sy;
      if (!g.moved && Math.hypot(dx, dy) > 7) g.moved = true;
      if (g.moved) {
        const sc = geom.current.s0 * geom.current.v.zoom;
        setView(clampView({ zoom: geom.current.v.zoom, cx: g.cx - dx / sc, cy: g.cy - dy / sc }));
      }
    }
  };
  const onUp = (e: RPointerEvent) => {
    const had = pts.current.delete(e.pointerId);
    if (pts.current.size < 2) pinch.current = null;
    const g = gesture.current;
    if (!had || !g) return;
    if (pts.current.size === 0) {
      gesture.current = null;
      if (!g.moved && e.type === 'pointerup') {
        if (g.aid) onTapArmy(g.aid);
        else if (g.hid) onTapHolding(g.hid);
        else setPanel(null);
      }
    }
  };

  /** Keep a tapped point clear of the bottom sheet (mobile) by nudging the view when it would sit underneath. */
  const revealPoint = (x: number, y: number) => {
    const g = geom.current;
    if (g.size.w >= 640) return;
    const sc = g.s0 * g.v.zoom;
    const sy = g.size.h / 2 + (y - g.v.cy) * sc;
    const limit = g.size.h * 0.5;
    if (sy > limit) setView(clampView({ zoom: g.v.zoom, cx: g.v.cx, cy: g.v.cy + (sy - g.size.h * 0.32) / sc }));
    void x;
  };
  const onTapHolding = (id: Id) => {
    if (geom.current.size.w < 640) setRealms(false);
    setPanel({ kind: 'holding', id });
    const hd = byId.get(id)?.holding;
    if (hd) revealPoint(hd.x, hd.y);
  };
  const onTapArmy = (id: Id) => {
    if (geom.current.size.w < 640) setRealms(false);
    const a = w?.armies.find((x) => x.id === id);
    if (!a) return;
    if (a.houseId === myHouseId) selectArmy(a.id === selectedArmyId ? null : a.id);
    setPanel({ kind: 'army', id });
    const hd = byId.get(a.locationId)?.holding;
    if (hd) revealPoint(hd.x, hd.y);
  };

  useEffect(() => {
    if (!focusId) return;
    const id = window.setTimeout(() => setFocusId(null), 4200);
    return () => window.clearTimeout(id);
  }, [focusId]);

  /** Pan and zoom to a title: a holding opens its panel, a region (kingdom, duchy, county) is fitted and outlined. */
  const showTitle = (id: Id) => {
    const x = byId.get(id);
    if (!x) return;
    const pts: Pt[] = [];
    for (const h of holdings) {
      for (let c: TitleSummary | undefined = h, g = 0; c && g < 8; g++) {
        if (c.id === id) { pts.push({ x: h.holding!.x, y: h.holding!.y }); break; }
        c = c.deJureParentId ? byId.get(c.deJureParentId) : undefined;
      }
    }
    if (pts.length === 0) return;
    const g = geom.current;
    const wide = g.size.w >= 640;
    const panelW = wide ? 380 : 0;
    const x0 = Math.min(...pts.map((p) => p.x));
    const x1 = Math.max(...pts.map((p) => p.x));
    const y0 = Math.min(...pts.map((p) => p.y));
    const y1 = Math.max(...pts.map((p) => p.y));
    const pad = land.spacing * 0.75;
    const single = !!x.holding;
    const target = Math.min((g.size.w - panelW - 48) / (x1 - x0 + 2 * pad), (g.size.h - TOP_INSET - 40) / (y1 - y0 + 2 * pad));
    const zoom = Math.max(MIN_ZOOM, Math.min(single ? 3.2 : 6, target / g.s0));
    const sc = g.s0 * zoom;
    const cy = single && !wide ? y0 + (g.size.h * 0.18) / sc : (y0 + y1) / 2 - (TOP_INSET / 2) / sc;
    setView(clampView({ zoom, cx: (x0 + x1) / 2 - panelW / 2 / sc, cy }));
    if (single) { setFocusId(null); setPanel({ kind: 'holding', id }); } else { setPanel(null); setFocusId(id); }
    if (!wide) setRealms(false);
  };

  const centerOnMine = () => {
    if (me) { setView(clampView({ cx: me.x, cy: me.y, zoom: Math.max(2, geom.current.v.zoom) })); return; }
    const mine = holdings.filter((h) => h.holderHouseId === myHouseId);
    if (mine.length === 0) { setView(null); return; }
    const cx = mine.reduce((s, h) => s + h.holding!.x, 0) / mine.length;
    const cy = mine.reduce((s, h) => s + h.holding!.y, 0) / mine.length;
    setView(clampView({ cx, cy, zoom: Math.max(2, geom.current.v.zoom) }));
  };

  if (world.isLoading || house.isLoading) return <Loading />;
  if (world.isError || !w) return <ErrorBox error={world.error} onRetry={() => world.refetch()} />;

  if (params.get('view') === 'military') {
    return (
      <div className="map-subview">
        <div className="map-subnav screen-top">
          <Segmented<'world' | 'military'> value="military" onChange={setSubview} options={[
            { value: 'world', label: t('nav.world') },
            { value: 'military', label: t('nav.military') },
          ]} />
        </div>
        <MilitaryScreen />
      </div>
    );
  }

  const selPath = selArmy ? [selArmy.locationId, ...selArmy.path] : [];
  const selD = selPath.slice(1).map((id, i) => roadCurve(selPath[i], id, byId)).filter(Boolean);
  const vb = `${v.cx - vw / 2} ${v.cy - vh / 2} ${vw} ${vh}`;
  const showCount = mapMode === 'armies' || scale > 1.1;
  const sea = { x: bounds.x0 - 3000, y: bounds.y0 - 3000, width: bw + 6000, height: bh + 6000 };
  const z = v.zoom;
  // When zoomed in far enough that banners have faded, name the kingdom under the view centre in the tool row.
  let here: { name: string; color: string } | null = null;
  if (z >= 2.4 && items.length > 0) {
    let bi = 0;
    let bd = Infinity;
    holdings.forEach((h, i) => { const d = Math.hypot(h.holding!.x - v.cx, h.holding!.y - v.cy); if (d < bd) { bd = d; bi = i; } });
    const kid = lineage[bi].kingdom;
    const kt = byId.get(kid);
    if (kt?.rank === 'kingdom') here = { name: kt.name.replace(/^Kingdom of /, ''), color: kingdomColor(kid) };
  }
  // Kingdom banners dominate the overview, shrink as you close in and fade once duchy names take over.
  const bannerK = z <= 1.6 ? 1 : Math.max(0.74, 1 - (z - 1.6) * 0.11);
  const bannerOpacity = z <= 2.6 ? 1 : Math.max(0, 1 - (z - 2.6) / 2.2);

  return (
    <div className={`map-wrap mode-${mapMode}${panel || realms ? ' has-panel' : ''}`} ref={wrapRef} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
      <svg className="map-svg" viewBox={vb} role="img" aria-label={t('map.aria')}>
        <MapDefs />
        <rect {...sea} fill="url(#mk-sea)" />
        <rect {...sea} fill="url(#mk-waves)" />

        <LandLayer cells={land.cells} fills={fills} glyphs={glyphs} borders={borders} mineEdges={mineEdges} kGlow={kGlow} focusEdges={focusEdges} mode={mapMode} />

        <g className="region-labels" style={{ pointerEvents: 'none' }}>
          {regions.map((r) => {
            if (r.rank === 'kingdom') return null;
            const show = (!multiKingdom || scale >= 0.85) && scale < 4;
            if (!show) return null;
            const pos = regionPos.get(r.id);
            if (!pos) return null;
            const px = 13;
            return (
              <text key={r.id} className={`reg-label ${r.rank}`} x={pos.x} y={pos.y} dy={px * 0.35 * u} textAnchor="middle" fontSize={px * u} letterSpacing={px * u * 0.26} strokeWidth={3 * u}>
                {r.name}
              </text>
            );
          })}
        </g>

        <RoadsLayer roads={w.roads} byId={byId} u={qU} />
        {scale > 1.5 && (
          <g style={{ pointerEvents: 'none' }}>
            {w.roads.map((r) => {
              const cv = roadCurve(r.a, r.b, byId);
              if (!cv) return null;
              const m = curvePoint(cv, 0.5);
              return <text key={`${r.a}-${r.b}`} className="road-label" x={m.x} y={m.y} fontSize={10 * u} strokeWidth={3 * u} textAnchor="middle" dy={-3 * u}>{t('map.roadDays', { count: r.days })}</text>;
            })}
          </g>
        )}

        {selD.length > 0 && (
          <g fill="none" strokeLinecap="round" style={{ pointerEvents: 'none' }}>
            {selD.map((cv, i) => <path key={`c${i}`} d={cv!.d} stroke={INK} strokeOpacity={0.7} strokeWidth={7 * u} />)}
            {selD.map((cv, i) => <path key={`g${i}`} className="route-flow" d={cv!.d} stroke={GOLD} strokeWidth={4 * u} strokeDasharray={`${6 * u} ${6 * u}`} />)}
          </g>
        )}

        {bannerOpacity > 0.02 && (
          <g className="kingdom-banners" style={{ pointerEvents: 'none' }} opacity={bannerOpacity}>
            {[...banners.values()].map((b) => {
              const pos = bannerPos.get(b.id);
              return pos ? <g key={b.id} transform={`translate(${pos.x} ${pos.y}) scale(${u * bannerK * pos.k0})`}><KingdomBanner b={b} /></g> : null;
            })}
          </g>
        )}

        <HoldingsLayer items={items} u={qU} scale={qScale} spacing={land.spacing} labels={labels.ids} selId={selHoldingId} targets={targets} showDays={z > 1.3} />
        <ArmiesLayer armyAt={armyAt} byId={byId} items={itemById} u={qU} scale={qScale} spacing={land.spacing} myHouseId={myHouseId} hostile={hostile}
          selectedArmyId={selectedArmyId} panelArmy={panel?.kind === 'army' ? panel.id : null} showCount={showCount} />
        {me && (() => {
          const it = itemById.get(me.locId);
          const px = it ? iconPx(qScale, it.tier, land.spacing) : 20;
          const lift = px * 0.62 + 24;
          const tip = me.armyCount > 0 ? -(lift + 22) : -px * (it && it.tier >= 2 ? 1.02 : 0.72) - 2;
          const offX = me.idx >= 0 ? (me.idx - (me.n - 1) / 2) * 34 * qU : 0;
          return <YouMarker x={me.x + offX} y={me.y} tipY={tip} u={qU} seed={me.seed} label={t('common.you')} />;
        })()}
      </svg>

      <div className="map-controls map-ui">
        <div className="map-row">
          <div className="map-subnav">
            <Segmented<'world' | 'military'> value="world" onChange={setSubview} options={[
              { value: 'world', label: t('nav.world') }, { value: 'military', label: t('nav.military') },
            ]} />
          </div>
          <div className="map-tools">
            <button type="button" className={`map-legend-btn ${legend ? 'on' : ''}`} aria-expanded={legend} onClick={() => { setLegend((x) => !x); }}>{t('map.legend.title')}</button>
            <button type="button" className={`map-legend-btn realms ${realms ? 'on' : ''}`} aria-expanded={realms} onClick={() => { setRealms((x) => !x); setLegend(false); }}>
              <IconRealm size={18} /><span className="lbl">{t('map.realms.button')}</span>
            </button>
          </div>
        </div>
        <Segmented<MapMode> value={mapMode} onChange={setMapMode} options={[
          { value: 'realm', label: t('map.mode.realm') }, { value: 'terrain', label: t('map.mode.terrain') }, { value: 'armies', label: t('map.mode.armies') },
        ]} />
        {here && (
          <span className="map-here-chip" style={{ ['--kc' as string]: here.color }} title={here.name}>
            <IconCrown size={14} /><span>{here.name}</span>
          </span>
        )}
      </div>
      <div className="map-zoom map-ui">
        <button type="button" aria-label={t('map.zoomIn')} onClick={() => zoomAt(size.w / 2, size.h / 2, v.zoom * 1.5)}>+</button>
        <button type="button" aria-label={t('map.zoomOut')} onClick={() => zoomAt(size.w / 2, size.h / 2, v.zoom / 1.5)}>&minus;</button>
        <button type="button" aria-label={t('map.fit')} title={t('map.fit')} onClick={() => setView(null)}>&#x2922;</button>
        <button type="button" className="map-here" aria-label={t('map.here')} title={t('map.here')} onClick={centerOnMine}><svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 21s-6.5-6-6.5-11a6.5 6.5 0 0 1 13 0c0 5-6.5 11-6.5 11z" /><circle cx="12" cy="10" r="2.4" /></svg></button>
      </div>
      <div className="map-legend-wrap map-ui">
        {legend && (
          <ul className="map-legend">
            <li><svg className="lg-ico wide" viewBox="-20 -13 40 26"><path d="M-17 -7H-12V9H-17L-14.5 1Z M17 -7H12V9H17L14.5 1Z" fill="#1d3f78" stroke={INK} strokeWidth={2} strokeLinejoin="round" /><rect x={-14} y={-9} width={28} height={16} rx={3} fill="#2a5ba8" stroke={INK} strokeWidth={2.4} /><use href="#mk-crown" transform="translate(0 15) scale(0.62)" /></svg>{t('map.legend.kingdom')}</li>
            <li><i className="lg kingdom-border" />{t('map.legend.kingdomBorder')}</li>
            <li><i className="lg duchy-tints" />{t('map.legend.duchyTints')}</li>
            <li><svg className="lg-ico" viewBox="-26 -50 52 54"><path d="M0,0C-6,-9 -23,-21 -23,-35A23,23 0 0 1 23,-35C23,-21 6,-9 0,0Z" fill={GOLD} stroke={INK} strokeWidth={5} strokeLinejoin="round" /><circle cx={0} cy={-35} r={14} fill="#fff7dc" stroke={INK} strokeWidth={3.5} /></svg>{t('map.legend.marker')}</li>
            <li><i className="lg gold-border" />{t('map.legend.you')}</li>
            <li><svg className="lg-ico" viewBox="-15 -19 30 30"><use href="#mk-keep" style={{ ['--acc' as string]: '#7fa3dc' }} /></svg>{t('map.legend.holding')}</li>
            <li><svg className="lg-ico" viewBox="-15 -19 30 30"><use href="#mk-city" style={{ ['--acc' as string]: '#7fa3dc' }} /></svg>{t('map.legend.city')}</li>
            <li><svg className="lg-ico" viewBox="-15 -29 30 40"><use href="#mk-keep" style={{ ['--acc' as string]: '#7fa3dc' }} /><use href="#mk-crown" /></svg>{t('map.legend.capital')}</li>
            <li><svg className="lg-ico" viewBox="-15 -19 30 30"><use href="#mk-site" /></svg>{t('map.legend.site')}</li>
            <li><i className="lg road" />{t('map.legend.road')}</li>
            <li><svg className="lg-ico" viewBox="-14 -18 28 36"><use href="#mk-shield" style={{ ['--col' as string]: GOLD }} /></svg>{t('military.army')}</li>
            <li><svg className="lg-ico" viewBox="-14 -18 28 36"><use href="#mk-shield" style={{ ['--col' as string]: ENEMY }} /></svg>{t('map.legend.enemy')}</li>
            <li><svg className="lg-ico" viewBox="-14 -18 28 36"><use href="#mk-shield" style={{ ['--col' as string]: NEUTRAL }} /></svg>{t('map.legend.other')}</li>
            <li><i className="lg siege" />{t('map.legend.siege')}</li>
            <li><i className="lg target" />{t('map.legend.target')}</li>
            {mapMode === 'terrain' && Object.keys(TERRAIN_FILL).map((k) => (
              <li key={k}><i className="lg terr" style={{ background: TERRAIN_FILL[k] }} />{t(`terrain.${k}`)}</li>
            ))}
          </ul>
        )}
      </div>

      {realms && (
        <div className="map-sheet map-ui" role="dialog" aria-label={t('map.realms.title')}>
          <RealmsPanel titles={w.titles} kingdomIds={w.kingdomIds ?? []} fill={w.kingdoms} houses={w.houses} myHouseId={myHouseId} heldTitleIds={house.data?.heldTitleIds ?? []}
            kingdomColor={kingdomColor} frontier={w.frontier} day={w.day} onShow={showTitle} onClose={() => setRealms(false)} />
        </div>
      )}

      {selArmy && !panel && (
        <div className="map-hint map-ui" role="status">
          <IconShield size={22} />
          <span className="grow">{t('map.hintMove', { name: selArmy.commanderName ?? t('military.army') })}</span>
          <Btn small variant="quiet" onClick={() => selectArmy(null)}>{t('common.cancel')}</Btn>
        </div>
      )}

      {panel && (
        <div className="map-panel map-ui" role="dialog" aria-label={t('map.details')}>
          {panel.kind === 'holding'
            ? <HoldingPanel id={panel.id} byId={byId} world={w} armyAt={armyAt} onClose={() => setPanel(null)} />
            : <ArmyPanel army={w.armies.find((a) => a.id === panel.id)} world={w} onClose={() => setPanel(null)} />}
        </div>
      )}
    </div>
  );
}

/** Label priority: selected, ours, capitals/cities, then the most developed. */
function score(it: Item, sel: Id | null): number {
  return (it.id === sel ? 1000 : 0) + (it.mine ? 200 : 0) + it.tier * 40 + (it.kind === 'city' ? 20 : 0) + it.dev;
}

/** Character ids of the opposing war leaders in our active wars. */
function hostileOwners(w: WorldView, wars: { attackerId: Id; defenderId: Id; endedDay: number | null }[], mine: Set<Id>): Set<Id> {
  const out = new Set<Id>();
  for (const war of wars) {
    if (war.endedDay !== null) continue;
    if (mine.has(war.attackerId)) out.add(war.defenderId);
    if (mine.has(war.defenderId)) out.add(war.attackerId);
  }
  // Armies owned by vassals/allies of the opposing leader are not resolvable client-side; leaders suffice for the map tint.
  void w;
  return out;
}

function HoldingPanel({ id, byId, world, armyAt, onClose }: {
  id: Id; byId: Map<Id, TitleSummary>; world: WorldView; armyAt: Map<Id, ArmyView[]>; onClose: () => void;
}) {
  const { t } = useTranslation();
  const house = useHouse();
  const cmd = useCommand();
  const selectedArmyId = useUi((s) => s.selectedArmyId);
  const selectArmy = useUi((s) => s.selectArmy);
  const hourEta = useHourEta();
  const x = byId.get(id);
  const hd = x?.holding;
  const army = house.data?.armies.find((a) => a.id === selectedArmyId) ?? null;
  const route = useMemo(() => (army ? shortestPath(world.roads, army.locationId, id) : null), [army, world.roads, id]);
  if (!x || !hd) return null;
  const here = armyAt.get(id) ?? [];
  const built = Object.entries(hd.buildings).filter(([, l]) => (l ?? 0) > 0);
  const owner = army ? army.ownerId : '';
  const actor = house.data && memberIds(house.data).has(owner) ? owner : actorOf(house.data);
  const sameSpot = army?.locationId === id;

  return (
    <div className="mp">
      <div className="mp-head">
        <span className="army-ico" aria-hidden="true"><IconShield /></span>
        <div className="grow">
          <Link to={`/title/${x.id}`} className="rankname link">{x.name}</Link>
          <div className="small muted">{RANK_NAMES[x.rank].title} · {t(`terrain.${hd.terrain}`)} · {t('map.dev', { n: hd.development })}</div>
        </div>
        <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common.close')}><IconX size={20} /></button>
      </div>
      {x.frontier && <SettlePanel site={x} />}
      {x.frontier?.settled !== false && (
        <>
          <div className="small"><HolderLine id={x.holderId} houseId={x.holderHouseId} name={x.holderName} /></div>
          <div className="mp-chips">
            <Badge tone="blue">{t('map.garrison', { n: hd.garrison })}</Badge>
            <Badge tone="blue">{t('map.levies', { n: hd.levyPool })}</Badge>
            {built.map(([k, l]) => <Badge key={k}>{t(`building.${k}`)} {l}</Badge>)}
          </div>
        </>
      )}
      {hd.construction && <div className="small muted">{t('map.building', { name: t(`building.${hd.construction.building}`), date: hd.construction.doneHour != null ? hourEta(hd.construction.doneHour).full : formatDate(hd.construction.doneDay) })}</div>}
      {here.length > 0 && (
        <div className="mp-armies">
          {here.map((a) => {
            const own = a.houseId === house.data?.house.id;
            return (
              <div key={a.id} className="mp-army">
                <span className="grow">{own ? <Badge tone="gold">{t('common.you')}</Badge> : null} {a.ownerName} · {t('map.men', { n: a.size })}</span>
                {own && <Btn small variant={a.id === selectedArmyId ? 'quiet' : 'ghost'} onClick={() => selectArmy(a.id === selectedArmyId ? null : a.id)}>{a.id === selectedArmyId ? t('map.deselect') : t('map.select')}</Btn>}
              </div>
            );
          })}
        </div>
      )}
      {army && !sameSpot && (
        <div className="mp-march">
          {route ? (
            <>
              <p className="small" style={{ marginBottom: 6 }}>
                {t('map.eta', { days: route.days, date: formatDate(world.day + route.days), eta: formatEta(msForDays(world, route.days), t as never) })}
                {route.path.length > 1 && <>{' '}{t('map.etaVia', { stops: route.path.slice(0, -1).map((p) => byId.get(p)?.name ?? p).join(' → ') })}</>}
              </p>
              <Btn variant="primary" block busy={cmd.pending}
                onClick={async () => { const r = await cmd.run({ type: 'move_army', actorId: actor, armyId: army.id, targetId: id }); if (r.ok) { selectArmy(null); onClose(); } }}>
                {t('map.marchHere', { name: army.commanderName ?? t('military.army') })}
              </Btn>
            </>
          ) : <p className="small muted">{t('map.noRoad')}</p>}
          <Inline error={cmd.error} />
        </div>
      )}
      <Link className="btn small quiet block" to={`/title/${x.id}`}>{t('map.open')}<IconChevron size={18} /></Link>
    </div>
  );
}

function ArmyPanel({ army, world, onClose }: { army: ArmyView | undefined; world: WorldView; onClose: () => void }) {
  const { t } = useTranslation();
  const house = useHouse();
  const selectedArmyId = useUi((s) => s.selectedArmyId);
  const selectArmy = useUi((s) => s.selectArmy);
  const eta = useEta();
  const hourEta = useHourEta();
  if (!army) return null;
  const own = army.houseId === house.data?.house.id;
  const loc = world.titles.find((x) => x.id === army.locationId);
  const dest = army.path.length ? world.titles.find((x) => x.id === army.path[army.path.length - 1]) : null;
  return (
    <div className="mp">
      <div className="mp-head">
        <span className="army-ico" aria-hidden="true"><IconShield /></span>
        <div className="grow">
          <div className="rankname">{t('map.armyOf', { name: army.ownerName })}</div>
          <div className="small muted">{t('map.men', { n: army.size })} · {t('military.morale')} {Math.round(army.morale * 100)}%</div>
        </div>
        <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common.close')}><IconX size={20} /></button>
      </div>
      <div className="mp-chips"><Badge tone="blue">{t(`formation.${army.formation}`)}</Badge><Badge tone="blue">{t(`posture.${army.posture}`)}</Badge>{army.siege && <Badge tone="red">{t('map.siege', { pct: Math.round(army.siege.progress * 100) })}</Badge>}</div>
      <div className="small">{t('map.at', { name: loc?.name ?? army.locationId })}{dest && army.nextArrivalDay != null && <> · {t('map.heading', { name: dest.name, eta: army.nextArrivalHour != null ? hourEta(army.nextArrivalHour).full : eta(army.nextArrivalDay) })}</>}{army.siege?.doneHour != null && <> · {t('map.siegeDone', { when: hourEta(army.siege.doneHour).full })}</>}</div>
      {own && (
        <div className="war-actions">
          <Btn small variant={selectedArmyId === army.id ? 'quiet' : 'primary'} onClick={() => { if (selectedArmyId === army.id) selectArmy(null); else { selectArmy(army.id); onClose(); } }}>
            {selectedArmyId === army.id ? t('map.deselect') : t('map.chooseDestination')}
          </Btn>
          <Link className="btn small ghost" to="/map?view=military">{t('map.manage')}</Link>
        </div>
      )}
    </div>
  );
}
